import React from 'react';
import clsx from 'clsx';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faExclamation} from '@fortawesome/pro-solid-svg-icons/faExclamation';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	Switch,
	type SwitchProps,
	switchClasses,
} from '@netx/core-components/Switch';
import {
	statusSwitchClasses,
	getStatusSwitchUtilityClass,
	STATUS_SWITCH_CLASS_NAME,
	type StatusSwitchClasses,
} from './statusSwitchClasses';

export type StatusSwitchOwnerState = WithClasses<
	StatusSwitchClasses,
	{
		error?: boolean;
	}
>;

const useUtilityClasses = (ownerState: StatusSwitchOwnerState) => {
	const {classes, error} = ownerState;
	const slots = {
		root: ['root', error && 'error'],
	};
	return composeClasses(slots, getStatusSwitchUtilityClass, classes);
};

const StatusSwitchRoot = styled(Switch)(({theme}) => ({
	// we want to support the error color even in an off state
	// mui only reflects colors when the switch is on
	[`&.${statusSwitchClasses.error} .${switchClasses.track}`]: {
		backgroundColor: theme.palette.error.main,
	},
}));

// since we are overriding the `StatusSwitchThumb` component to inject an icon
// we need to add some styles to get this matching the design we want here
const StatusSwitchThumb = styled('div', {
	name: STATUS_SWITCH_CLASS_NAME,
	slot: 'Thumb',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxShadow: theme.shadows[1],
	backgroundColor: theme.palette.error.main,
	width: 20,
	height: 20,
	borderRadius: '50%',
	fontSize: 10,
	svg: {
		color: theme.palette.error.contrastText,
	},
}));

export interface StatusSwitchProps
	extends Partial<Omit<SwitchProps, 'classes'>>,
		WithClasses<StatusSwitchClasses> {
	/**
	 * Tooltip label
	 */
	label?: string;
	/**
	 * Can the switch be interacted with?
	 * If `false`, the switch is disabled and can't be toggled
	 */
	canBeEnabled?: boolean;
	/**
	 * This is kind of a misnomer here
	 * This means that we are considering the switch to be off for reasons other than user iteraction
	 * To control whether the user can interact with the switch, use `canBeEnabled`
	 * @TODO: it may be worth reqorking this to avoid confusion - basically switch the use of `disabled` and `canBeEnabled`
	 */
	diabled?: boolean;
}

export const StatusSwitchPrimitive = (
	{
		classes: _classes,
		className,
		checked,
		label,
		canBeEnabled = true,
		disabled,
		...props
	}: StatusSwitchProps,
	ref: React.ForwardedRef<HTMLButtonElement>,
) => {
	const ownerState: StatusSwitchOwnerState = {
		classes: _classes,
		className,
		error: disabled,
	};

	const classes = useUtilityClasses(ownerState);

	// if disabled, we want to add an icon to the thumb
	if (disabled) {
		props.icon = (
			<StatusSwitchThumb title={label}>
				<FontAwesomeIcon icon={faExclamation} />
			</StatusSwitchThumb>
		);
	}

	return (
		<StatusSwitchRoot
			title={label}
			{...props}
			color={disabled ? 'error' : props.color}
			checked={checked}
			disabled={!canBeEnabled}
			className={clsx(classes.root, className)}
			ref={ref}
		/>
	);
};

/**
 * A status switch that supports a disabled (error) state
 */
export const StatusSwitch = React.forwardRef(StatusSwitchPrimitive) as (
	props: StatusSwitchProps,
) => JSX.Element;

export default StatusSwitch;
